// src/Components/Private/_Dashboard/SupportBotWidget.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const getInitialPosition = () => {
  if (typeof window === "undefined") {
    return { x: 16, y: 16 };
  }
  try {
    const stored = localStorage.getItem("WAM_BOT_POS");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed.x === "number" &&
        typeof parsed.y === "number"
      ) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn("No se pudo leer WAM_BOT_POS:", e);
  }
  const padding = 24;
  const defaultX = window.innerWidth - 380 - padding; // aprox ancho ventana
  const defaultY = window.innerHeight - 120;
  return { x: defaultX, y: defaultY };
};

const QUICK_QUESTIONS = [
  "¿Cómo genero una liquidación de expensas?",
  "¿Cómo cargo una factura de gasto?",
  "¿Cómo creo un consorcio nuevo?",
];

const getStoredToken = () =>
  localStorage.getItem("USER_AUTH") ||
  sessionStorage.getItem("USER_AUTH") ||
  "";

const useSupportBotChat = ({ defaultContext } = {}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  const ensureWelcome = useCallback(() => {
    if (hasWelcomed) return;
    const welcomeMsg = {
      id: Date.now(),
      role: "assistant",
      content:
        "Hola 👋 Soy el asistente IA de WAM. Puedo guiarte paso a paso sobre cómo usar el sistema: " +
        "crear consorcios, cargar gastos, generar liquidaciones, conciliaciones, reclamos, etc.",
    };
    setMessages((prev) => [...prev, welcomeMsg]);
    setHasWelcomed(true);
  }, [hasWelcomed]);

  const sendQuestion = useCallback(
    async (question, metaOverride = null) => {
      const cleaned = (question || "").trim();
      if (!cleaned || loading) return;

      const userMsg = {
        id: Date.now(),
        role: "user",
        content: cleaned,
      };

      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);
      setError(null);

      try {
        const token = getStoredToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const payload = { question: cleaned };
        const contextPayload = metaOverride || defaultContext;
        if (contextPayload) {
          payload.context = contextPayload;
        }

        const resp = await axios.post("/bot/ask/", payload, { headers });

        const data = resp.data || {};
        const answerText = data.answer || "No recibí respuesta del asistente.";
        const audioUrl = data.audio_url || null;

        const botMsg = {
          id: Date.now() + 1,
          role: "assistant",
          content: answerText,
          audioUrl,
        };

        setMessages((prev) => [...prev, botMsg]);
      } catch (err) {
        console.error("Error en SupportBot:", err);

        if (err.response && err.response.status === 401) {
          setError(
            "Tu sesión no está activa o el token venció. Cerrá sesión y volvé a entrar."
          );
        } else {
          setError(
            "No pude obtener respuesta del asistente. Intentá de nuevo más tarde."
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [defaultContext, loading]
  );

  const handleSubmit = useCallback(
    (event) => {
      event?.preventDefault();
      if (!input.trim()) return;
      const q = input;
      setInput("");
      sendQuestion(q);
    },
    [input, sendQuestion]
  );

  const handleQuickQuestion = useCallback(
    (q) => {
      if (loading) return;
      sendQuestion(q);
    },
    [loading, sendQuestion]
  );

  return {
    messages,
    input,
    setInput,
    loading,
    error,
    quickQuestions: QUICK_QUESTIONS,
    handleSubmit,
    handleQuickQuestion,
    messagesEndRef,
    ensureWelcome,
    hasWelcomed,
  };
};

const SupportBotChatBody = ({
  messages,
  loading,
  error,
  quickQuestions,
  onQuickQuestion,
  input,
  setInput,
  onSubmit,
  messagesEndRef,
  hasWelcomed,
}) => (
  <>
    <div className="wam-bot-messages">
      {messages.length === 0 && !hasWelcomed && (
        <div className="wam-bot-empty">
          Hola 👋 Soy el asistente de WAM. Preguntame cómo hacer algo en el
          sistema y te guío paso a paso.
        </div>
      )}

      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`wam-bot-message-row ${
            msg.role === "user"
              ? "wam-bot-message-row-user"
              : "wam-bot-message-row-assistant"
          }`}
        >
          {msg.role === "assistant" && (
            <div className="wam-bot-message-avatar">🤖</div>
          )}
          <div>
            <div
              className={`wam-bot-bubble ${
                msg.role === "user"
                  ? "wam-bot-bubble-user"
                  : "wam-bot-bubble-assistant"
              }`}
            >
              {msg.content}
            </div>

            {msg.role === "assistant" && msg.audioUrl && (
              <audio controls style={{ marginTop: 4, width: "100%" }}>
                <source src={msg.audioUrl} type="audio/mpeg" />
                Tu navegador no soporta audio HTML5.
              </audio>
            )}
          </div>
        </div>
      ))}
      {loading && (
        <div className="wam-bot-typing-row">
          <div className="wam-bot-message-avatar">🤖</div>
          <div className="wam-bot-typing-bubble">
            <span className="wam-bot-dot" />
            <span className="wam-bot-dot" />
            <span className="wam-bot-dot" />
          </div>
        </div>
      )}

      {error && <div className="wam-bot-error">{error}</div>}

      <div ref={messagesEndRef} />
    </div>

    <div className="wam-bot-quick-questions">
      {quickQuestions.map((q) => (
        <button
          key={q}
          type="button"
          className="wam-bot-chip"
          onClick={() => onQuickQuestion(q)}
          disabled={loading}
        >
          {q}
        </button>
      ))}
    </div>

    <form className="wam-bot-input-row" onSubmit={onSubmit}>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Escribí tu pregunta…"
        className="wam-bot-input"
      />
      <button
        type="submit"
        className="wam-bot-send-btn"
        disabled={loading || !input.trim()}
      >
        ➤
      </button>
    </form>
  </>
);

const SupportBotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(getInitialPosition);
  const positionRef = useRef(position);
  const [dragging, setDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const bot = useSupportBotChat();

  const toggleOpen = () => setIsOpen((prev) => !prev);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useEffect(() => {
    if (isOpen) {
      bot.ensureWelcome();
    }
  }, [isOpen, bot.ensureWelcome]);

  const startDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const e = event.touches ? event.touches[0] : event;
    dragOffsetRef.current = {
      x: e.clientX - positionRef.current.x,
      y: e.clientY - positionRef.current.y,
    };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (event) => {
      event.preventDefault();
      const e = event.touches ? event.touches[0] : event;

      let x = e.clientX - dragOffsetRef.current.x;
      let y = e.clientY - dragOffsetRef.current.y;

      const padding = 8;
      const width = isOpen ? 380 : 260;
      const height = isOpen ? 420 : 60;

      const maxX = window.innerWidth - width - padding;
      const maxY = window.innerHeight - height - padding;

      if (x < padding) x = padding;
      if (y < padding) y = padding;
      if (x > maxX) x = maxX;
      if (y > maxY) y = maxY;

      setPosition({ x, y });
    };

    const handleUp = () => {
      setDragging(false);
      try {
        localStorage.setItem(
          "WAM_BOT_POS",
          JSON.stringify(positionRef.current)
        );
      } catch (e) {
        console.warn("No se pudo guardar WAM_BOT_POS:", e);
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [dragging, isOpen]);

  return (
    <div
      style={{
        position: "fixed",
        left: position.x,
        top: position.y,
        zIndex: 9999,
        cursor: dragging ? "grabbing" : "default",
      }}
    >
      {!isOpen ? (
        <button
          onClick={toggleOpen}
          className="wam-bot-fab wam-bot-pulse"
          type="button"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        >
          <div className="wam-bot-fab-icon">🤖</div>
          <div className="wam-bot-fab-text">
            <span className="wam-bot-fab-title">Asistente WAM</span>
            <span className="wam-bot-fab-subtitle">¿Necesitás ayuda?</span>
          </div>
        </button>
      ) : (
        <div className="wam-bot-window">
          <div
            className="wam-bot-header"
            onMouseDown={startDrag}
            onTouchStart={startDrag}
          >
            <div className="wam-bot-header-left">
              <div className="wam-bot-avatar">🤖</div>
              <div>
                <div className="wam-bot-header-title">Asistente WAM</div>
                <div className="wam-bot-header-status">
                  En línea · Texto + Audio
                </div>
              </div>
            </div>
            <button
              type="button"
              className="wam-bot-header-close"
              onClick={toggleOpen}
            >
              ✕
            </button>
          </div>

          <SupportBotChatBody
            messages={bot.messages}
            loading={bot.loading}
            error={bot.error}
            quickQuestions={bot.quickQuestions}
            onQuickQuestion={bot.handleQuickQuestion}
            input={bot.input}
            setInput={bot.setInput}
            onSubmit={bot.handleSubmit}
            messagesEndRef={bot.messagesEndRef}
            hasWelcomed={bot.hasWelcomed}
          />
        </div>
      )}
    </div>
  );
};

export const SupportBotDrawerPanel = ({ onClose, contextMeta }) => {
  const chat = useSupportBotChat({ defaultContext: contextMeta });
  useEffect(() => {
    chat.ensureWelcome();
  }, [chat.ensureWelcome]);

  const subtitle = useMemo(() => {
    if (!contextMeta) return "En línea · Texto + Audio";
    const parts = [];
    if (contextMeta?.title) parts.push(contextMeta.title);
    if (contextMeta?.consorcio) parts.push(contextMeta.consorcio);
    return parts.join(" • ") || "En línea · Texto + Audio";
  }, [contextMeta]);

  return (
    <div
      className="wam-bot-window wam-bot-window--drawer"
      style={{ width: "100%" }}
    >
      <div className="wam-bot-header">
        <div className="wam-bot-header-left">
          <div className="wam-bot-avatar">🤖</div>
          <div>
            <div className="wam-bot-header-title">Asistente WAM</div>
            <div className="wam-bot-header-status">{subtitle}</div>
          </div>
        </div>
        <button
          type="button"
          className="wam-bot-header-close"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      <SupportBotChatBody
        messages={chat.messages}
        loading={chat.loading}
        error={chat.error}
        quickQuestions={chat.quickQuestions}
        onQuickQuestion={chat.handleQuickQuestion}
        input={chat.input}
        setInput={chat.setInput}
        onSubmit={chat.handleSubmit}
        messagesEndRef={chat.messagesEndRef}
        hasWelcomed={chat.hasWelcomed}
      />
    </div>
  );
};

export default SupportBotWidget;
